// src/components/Invoice/AgendaView.tsx
import React, { useState } from "react";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Badge } from "primereact/badge";
import { Button } from "../ui/Button";
import { useNavigate } from "react-router-dom";
import { Paginator } from "primereact/paginator";
import { Calendar } from "primereact/calendar";

interface PaginationMetadataDto {
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

interface AgendaItemDto {
  invoice_id: number;
  invoice_number: string;
  client_name: string;
  due_date: string;
  amount: number;
  payment_status: 'paid' | 'pending' | 'overdue' | 'draft' | 'partial';
  currency_symbol: string;
  days_until_due?: number;
  date: string;
  payment_date?: string;
}

interface AgendaViewProps {
  items: AgendaItemDto[];
  startDate: string;
  endDate: string;
  onMonthChange?: (month: number, year: number) => void;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  currentMonth: string;
  currentYear: number;
  loading?: boolean;
  pagination: PaginationMetadataDto;
  onPageChange?: (e:any) => void;
  onSort:(e:any)=>void;
  sortBy:any;
  sortField?:string
}

const AgendaView: React.FC<AgendaViewProps> = ({ 
  items, 
  startDate, 
  endDate,
  onMonthChange,
  onDateRangeChange,
  currentMonth,
  currentYear,
  loading = false,
  pagination,
  onPageChange,
  sortBy,
  onSort,
  sortField
}) => {
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date | null>(
  new Date(currentYear, monthNames.indexOf(currentMonth), 1)
);

  // Handle page change
  const handlePageChange = (event: DataTableStateEvent) => {
    if (onPageChange && event.page !== undefined) {
      onPageChange(event);
    }
  };

  const formatCurrency = (value: number, symbol: string = '$') => {
    return `${symbol}${value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      paid: { severity: 'success' as const, text: 'Paid' },
      pending: { severity: 'warning' as const, text: 'Pending' },
      partial: { severity: 'secondary' as const, text: 'Partial' },
      overdue: { severity: 'danger' as const, text: 'Overdue' },
      draft: { severity: 'info' as const, text: 'Draft' },
      write_off: { severity: 'info' as const, text: 'Write Off' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <Badge 
        value={config.text} 
        severity={config.severity}
        className="text-xs"
      />
    );
  };

  // Define the emptyMessage template function
  const emptyMessageTemplate = () => (
    <div className="text-center py-8">
      <div className="text-gray-400 mb-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="mx-auto mb-3"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      </div>
      <p className="text-gray-500 text-sm">
        No invoices found for the selected period
      </p>
      <p className="text-gray-400 text-xs mt-1">
        {formatDate(startDate)} - {formatDate(endDate)}
      </p>
    </div>
  );

  // Rest of your existing functions (getMonthBounds, handlePrevMonth, handleNextMonth, etc.)
  // ... [Include these functions from the original component]

  const invoiceNumberTemplate = (rowData: AgendaItemDto) => (
    <Button
      className="text-white-600 hover:text-white-800 p-0 h-auto font-thin"
      onClick={() => navigate(`/invoice/${rowData.invoice_id}`)}
    >
      {rowData.invoice_number}
    </Button>
  );

  const amountTemplate = (rowData: AgendaItemDto) => (
    <span className="font-medium">
      {formatCurrency(rowData.amount, rowData.currency_symbol)}
    </span>
  );

  const dueDateTemplate = (rowData: AgendaItemDto) => (
    <div className="flex flex-col">
      <span className="text-sm text-gray-500">
        {rowData.date}
      </span>
    </div>
  );

  const statusTemplate = (rowData: AgendaItemDto) => getStatusBadge(rowData.payment_status);

  const actionTemplate = (rowData: AgendaItemDto) => (
    <div className="flex gap-2">
      <Button
        size="sm"
        className="text-white-600 hover:text-white-800 p-1"
        title="View Invoice"
        onClick={() => navigate(`/invoice/${rowData.invoice_id}`)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      </Button>
    </div>
  );

  // Add the month navigation handlers here
  const getMonthBounds = (monthName: string, year: number) => {
    const monthIndex = monthNames.indexOf(monthName);
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    
    const formatDate = (date: Date): string => {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    return {
      start: formatDate(firstDay),
      end: formatDate(lastDay)
    };
  };

const handlePrevMonth = () => {
  const currentMonthIndex = monthNames.indexOf(currentMonth);
  let newMonth: number;
  let newYear: number;

  if (currentMonthIndex === 0) {
    newMonth = 12;
    newYear = currentYear - 1;
  } else {
    newMonth = currentMonthIndex;
    newYear = currentYear;
  }

  // Create a new date object and update the calendar
  const newDate = new Date(newYear, newMonth - 1, 1);
  setSelectedDate(newDate);

  const newMonthName = monthNames[newMonth - 1];
  const { start, end } = getMonthBounds(newMonthName, newYear);

  if (onMonthChange) {
    onMonthChange(newMonth, newYear);
  }

  if (onDateRangeChange) {
    onDateRangeChange(start, end);
  }
};

const handleNextMonth = () => {
  const currentMonthIndex = monthNames.indexOf(currentMonth);
  let newMonth: number;
  let newYear: number;

  if (currentMonthIndex === 11) {
    newMonth = 1;
    newYear = currentYear + 1;
  } else {
    newMonth = currentMonthIndex + 2;
    newYear = currentYear;
  }

  // Create a new date object and update the calendar
  const newDate = new Date(newYear, newMonth - 1, 1);
  setSelectedDate(newDate);

  const newMonthName = monthNames[newMonth - 1];
  const { start, end } = getMonthBounds(newMonthName, newYear);

  if (onMonthChange) {
    onMonthChange(newMonth, newYear);
  }

  if (onDateRangeChange) {
    onDateRangeChange(start, end);
  }
};

  const handleMonthSelection = (e: any) => {
  if (e.value) {
    const newDate = new Date(e.value);
    const newMonth = newDate.getMonth() + 1;
    const newYear = newDate.getFullYear();
    
    setSelectedDate(newDate);
    
    const newMonthName = monthNames[newMonth - 1];
    const { start, end } = getMonthBounds(newMonthName, newYear);
    
    if (onMonthChange) {
      onMonthChange(newMonth, newYear);
    }
    
    if (onDateRangeChange) {
      onDateRangeChange(start, end);
    }
  }
};

  return (
    <div className="agenda-view">
      <div className="mb-4 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">
            Payment Agenda
          </h3>
          <p className="text-sm text-gray-600">
            {currentMonth} {currentYear} • {pagination.totalRecords} invoices
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="rounded-md p-0.5 flex">
            {/* <button 
              className="px-3 py-1.5 rounded text-sm font-medium transition-colors border-gold text-white bg-gold"
            >
              Month
            </button> */}
            <Calendar value={selectedDate} onChange={handleMonthSelection} view="month" dateFormat="mm/yy" />
          </div>
          
          <div className="flex space-x-1">
            <button 
              className="p-1.5 rounded-md border-gold text-gray-700 hover:bg-gray-200 transition-colors flex items-center justify-center cursor-pointer"
              onClick={handlePrevMonth}
              aria-label="Previous month"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <button 
              className="p-1.5 rounded-md border-gold text-gray-700 hover:bg-gray-200 transition-colors flex items-center justify-center cursor-pointer"
              onClick={handleNextMonth}
              aria-label="Next month"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <DataTable
        value={items}
        emptyMessage={emptyMessageTemplate}
        lazy
        rows={pagination.pageSize}
        totalRecords={pagination.totalRecords}
        first={pagination.currentPage}
        onPage={handlePageChange}
        loading={loading}
        paginator={false}
        onSort={onSort}
        sortField={sortField}
        sortOrder={sortBy}
        className="agenda-table"
        rowClassName={(data) => {
          if (data.payment_status === 'overdue') return 'bg-red-50';
          if (data.days_until_due === 0) return 'bg-orange-50';
          return '';
        }}
        stripedRows
        responsiveLayout="scroll"
      >
        <Column
          field="invoice_number"
          header="Invoice #"
          body={invoiceNumberTemplate}
          sortable
          className="min-w-32"
        />
        <Column
          field="client_name"
          header="Client"
          sortable
          className="min-w-40"
        />
        <Column
          field="due_date"
          header="Due Date"
          body={dueDateTemplate}
          className="min-w-32"
        />
        <Column
          field="amount"
          header="Amount"
          body={amountTemplate}
          sortable
          className="min-w-28 text-right"
        />
        <Column
          field="status"
          header="Status"
          body={statusTemplate}
          className="min-w-24"
        />
        <Column
          header="Actions"
          body={actionTemplate}
          className="min-w-20"
          style={{ textAlign: 'center' }}
        />
      </DataTable>
      
      {/* Custom paginator */}
      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Showing {items.length > 0 ? (pagination.currentPage - 1) * pagination.pageSize + 1 : 0} 
          to {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalRecords)} 
          of {pagination.totalRecords} invoices
        </div>
        <Paginator 
          first={(pagination.currentPage - 1) * pagination.pageSize}
          rows={pagination.pageSize}
          totalRecords={pagination.totalRecords}
          onPageChange={(e) => {
            if (onPageChange) {
              onPageChange(e);
            }
          }}
          template="PrevPageLink PageLinks NextPageLink"
          className="p-0 border-0"
        />
      </div>
    </div>
  );
};

export default AgendaView;